import { useEffect, useState } from "react";
import { ShoppingCart, BookOpen, Store, BookMarked } from "lucide-react";
import { BOOK, getSaleState } from "@/lib/site";

function getRemaining() {
  const target = new Date(BOOK.launchWeekEnd).getTime();
  const now = Date.now();
  const diff = Math.max(0, target - now);
  return {
    diff,
    days: Math.floor(diff / (1000 * 60 * 60 * 24)),
    hours: Math.floor((diff / (1000 * 60 * 60)) % 24),
    minutes: Math.floor((diff / (1000 * 60)) % 60),
    seconds: Math.floor((diff / 1000) % 60),
  };
}

export default function Countdown() {
  const [t, setT] = useState(getRemaining());
  const [state, setState] = useState(getSaleState());

  useEffect(() => {
    const id = setInterval(() => {
      setT(getRemaining());
      setState(getSaleState());
    }, 1000);
    return () => clearInterval(id);
  }, []);

  const units = [
    { label: "Days", value: t.days },
    { label: "Hours", value: t.hours },
    { label: "Minutes", value: t.minutes },
    { label: "Seconds", value: t.seconds },
  ];

  const isPromo = state === "launchweek" && t.diff > 0;

  return (
    <section id="launch" className="relative z-10 bg-gold py-14 md:py-16" data-testid="launch-section">
      <div className="mx-auto max-w-7xl px-6 md:px-12">
        <div className="flex flex-col items-center gap-8 text-center lg:flex-row lg:justify-between lg:text-left">
          <div>
            <p className="font-body text-sm uppercase tracking-[0.25em] text-twilight/70">
              {isPromo
                ? "Available Now · Kindle Launch Sale"
                : "Available Now · Ebook, Paperback, and Hardcover"}
            </p>
            <h2 className="mt-2 font-display text-3xl font-bold text-twilight md:text-4xl" data-testid="launch-headline">
              {isPromo
                ? <>Kindle Launch Sale — Just {BOOK.price}</>
                : <>Available Now — Get Your Copy Today</>}
            </h2>
            <p className="mt-1 font-body text-twilight/80">
              {isPromo
                ? "$0.99 Kindle price runs July 24 – July 31, 2026. Grab your copy and, if it blesses you, please leave a quick review."
                : "Order the ebook, paperback, or hardcover edition from your favorite retailer."}
            </p>
            <div className="mt-4 flex flex-wrap justify-center gap-3 lg:justify-start" data-testid="countdown-buy-buttons">
              <a
                href={BOOK.amazon}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="countdown-buy-amazon"
                className="inline-flex items-center gap-2 rounded-full bg-twilight px-6 py-3 font-medium text-gold transition-[background-color,transform] duration-300 hover:bg-twilight-light active:scale-95"
              >
                <ShoppingCart className="h-4 w-4" /> Buy on Amazon
              </a>
              <a
                href={BOOK.barnesNoble}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="countdown-buy-bn"
                className="inline-flex items-center gap-2 rounded-full border border-twilight/30 px-6 py-3 font-medium text-twilight transition-[background-color] duration-300 hover:bg-twilight hover:text-gold"
              >
                <BookOpen className="h-4 w-4" /> Barnes &amp; Noble
              </a>
              <a
                href={BOOK.walmart}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="countdown-buy-walmart"
                className="inline-flex items-center gap-2 rounded-full border border-twilight/30 px-6 py-3 font-medium text-twilight transition-[background-color] duration-300 hover:bg-twilight hover:text-gold"
              >
                <Store className="h-4 w-4" /> Walmart
              </a>
              <a
                href={BOOK.kharis}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="countdown-buy-kharis"
                className="inline-flex items-center gap-2 rounded-full border border-twilight/30 px-6 py-3 font-medium text-twilight transition-[background-color] duration-300 hover:bg-twilight hover:text-gold"
              >
                <BookMarked className="h-4 w-4" /> Kharis Publishing
              </a>
            </div>
          </div>

          {isPromo && (
            <div className="flex gap-3 md:gap-4" data-testid="countdown-timer">
              {units.map((u) => (
                <div
                  key={u.label}
                  className="flex min-w-[68px] flex-col items-center rounded-2xl bg-twilight px-3 py-3 md:min-w-[84px] md:px-4 md:py-4"
                >
                  <span className="font-display text-3xl font-bold text-gold md:text-4xl tabular-nums">
                    {String(u.value).padStart(2, "0")}
                  </span>
                  <span className="mt-1 font-body text-[10px] uppercase tracking-[0.2em] text-cream/60 md:text-xs">
                    {u.label}
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </section>
  );
}
